import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommitteeMemberDto } from '../shared/dto/committee-member.dto';
import { ProfileRead } from 'src/app/profiles/shared/profile-read.model';
import { ProfileService } from 'src/app/profiles/shared/profile.service';
import { CommitteeDto } from '../shared/dto/committee.dto';
import { CommitteeService } from '../shared/committee.service';
import { TermSettingsEnum } from '../shared/enum/term-settings.enum';
import { TranslationService } from 'src/app/shared';
import { ControlContainer, NgForm } from '@angular/forms';
import { ObservationService } from 'src/app/shared/observation/observation.service';
import { Subscription } from 'rxjs';
import { SelectItem } from 'primeng/api';
import { Optional, SkipSelf } from '@angular/core';

@Component({
  selector: 'wl-committee-member-edit',
  templateUrl: './committee-member-edit.component.html',
  styleUrls: ['./committee-member-edit.component.less'],
  viewProviders: [
  {
    provide: ControlContainer,
    useFactory: (container: ControlContainer) => container,
    deps: [[new Optional(), new SkipSelf(), ControlContainer]]
  }],
    standalone: false
})
export class CommitteeMemberEditComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  get committeeMember() {
    return this._committeeMember;
  }
  set committeeMember(value: CommitteeMemberDto) {
    this._committeeMember = value;
    if (value?.ProfileID) {
      this.loadProfile(value.ProfileID);
    }
    if (value) {
      this.setDefaultValues();
    }
    this.committeeMemberChange.emit(this._committeeMember);
  }
  @Output() committeeMemberChange: EventEmitter<CommitteeMemberDto> = new EventEmitter();

  @Input() committeeId: number;

  profile: ProfileRead;
  committee: CommitteeDto;
  usesDefaultTerm = true;
  defaultTermText: string;
  neverExpires: boolean;
  representingOrganizationsTypeItems: SelectItem[] = [];
  usesDefaultRepresentingOrganization: boolean = true;
  representingOrganization;

  private _committeeMember: CommitteeMemberDto;
  private _subs = new Subscription();
  constructor(
    private _profileService: ProfileService,
    private _committeeService: CommitteeService,
    private _translationService: TranslationService,
    private _observationService: ObservationService
  ) {}

  async ngOnInit(): Promise<void> {
    this._subs.add(
      this._observationService.observeCommittees().subscribe((id) => {
        if (id === this.committeeId) {
          this._committeeService.get(id).then((committee) => {
            this.committee = committee;
            this.setDefaultTermText();
          });
        }
      })
    );
  }

  async ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges.committeeId && this.committeeId) {
      this.committee = await this._committeeService.get(this.committeeId);
      await this.setDefaultTermText();
    }
  }

  ngOnDestroy() {
    this._subs.unsubscribe();
  }

  useDefaultRepresentingOrganization() {
    if (this.committeeMember.RepresentingOrganizations) {
      let defaultOrg = this._committeeMember.RepresentingOrganizations.find((org) => org.IsPrimaryOrg === true);
      this.representingOrganization = this.representingOrganizationsTypeItems.find(
        (item) => item.value.OrgProfileID === defaultOrg.OrgProfileID
      );
      this.committeeMember.OrgName = this.representingOrganization.value.OrgName;
      this.committeeMember.OrgProfileID = this.representingOrganization.value.OrgProfileID;

      if (this.committeeMember.RepresentingOrganizations) {
        let defaultOrg = this.committeeMember.RepresentingOrganizations.find((org) => org.IsPrimaryOrg === true);

        if (defaultOrg.OrgProfileID === this.committeeMember.OrgProfileID) {
          this.usesDefaultRepresentingOrganization = true;
        }
      }
    }
    this.usesDefaultRepresentingOrganization = true;
  }

  updateRepresentingOrganization() {
    if (this.representingOrganization) {
      this.committeeMember.OrgName = this.representingOrganization.OrgName;
      this.committeeMember.OrgProfileID = this.representingOrganization.OrgProfileID;

      if (this.committeeMember.RepresentingOrganizations) {
        let defaultOrg = this.committeeMember.RepresentingOrganizations.find((org) => org.IsPrimaryOrg === true);

        if (defaultOrg.OrgProfileID === this.committeeMember.OrgProfileID) {
          this.usesDefaultRepresentingOrganization = true;
        }
      }
    }
  }

  useDefaultTerm() {
    this.usesDefaultTerm = true;
    const memberSince = new Date();
    const termExpires = new Date(memberSince);
    termExpires.setMonth(memberSince.getMonth() + this.committee.TermMonths);
    this.committeeMember.MemberSince = new Date(memberSince.getTime());
    this.committeeMember.TermExpires = new Date(memberSince.setMonth(memberSince.getMonth() + this.committee.TermMonths));
    this.neverExpires = this.committee.TermMonths === TermSettingsEnum.NeverExpires;
  }

  neverExpiresChanged() {
    if (this.neverExpires) {
      this.committeeMember.TermExpires = new Date(this.committeeMember.MemberSince);
    } else {
      const termExpires = new Date();
      termExpires.setMonth(termExpires.getMonth() + 1);
      this.committeeMember.TermExpires = termExpires;
    }
  }

  memberSinceChanged() {
    if (this.usesDefaultTerm) {
      const termExpires = new Date(this.committeeMember.MemberSince);
      termExpires.setMonth(termExpires.getMonth() + this.committee.TermMonths);
      this.committeeMember.TermExpires = termExpires;
    }
  }

  private loadProfile(profileId: number) {
    this._profileService.getProfile(profileId).then((profile) => {
      this.profile = profile;
      this._profileService.getMemberIconColor(profileId).then((color) => {
        this.profile.MemberTypeIconColor = color;
      });
    });
  }

  private setDefaultValues() {
    this.usesDefaultTerm = true;
    this.usesDefaultRepresentingOrganization = true;
    this.neverExpires = false;

    this.representingOrganizationsTypeItems = [];
    if (this.committeeMember.RepresentingOrganizations) {
      this.committeeMember.RepresentingOrganizations.forEach((organization) => {
        let representingOrg: SelectItem = {
          label: organization.OrgName,
          value: { OrgName: organization.OrgName, OrgProfileID: organization.OrgProfileID, IsPrimaryOrg: organization.IsPrimaryOrg },
        };
        this.representingOrganizationsTypeItems.push(representingOrg);
      });
    }

    if (!this.committeeMember.CommitteeMemberID) {
      this.useDefaultTerm();
    } else {
      const diffInMonths = this.differenceInMonths(this.committeeMember.MemberSince, this.committeeMember.TermExpires);
      if (diffInMonths === 0) {
        this.neverExpires = true;
      }
      if (diffInMonths !== this.committee.TermMonths) {
        this.usesDefaultTerm = false;
      }
    }

    if (this.committeeMember.OrgProfileID !== 0) {
      if (this.committeeMember.RepresentingOrganizations) {
        let defaultOrg = this.committeeMember.RepresentingOrganizations.find((org) => org.IsPrimaryOrg === true);
        if (this.committeeMember.OrgProfileID !== defaultOrg.OrgProfileID) {
          this.usesDefaultRepresentingOrganization = false;
        }
      }
    }
  }

  private async setDefaultTermText() {
    switch (this.committee.TermMonths) {
      case TermSettingsEnum.NeverExpires:
        this.defaultTermText = await this._translationService.getTitlecase('committees.edit.committee-details.terms.never-expires');
        break;
      case TermSettingsEnum.OneYear:
        this.defaultTermText = await this._translationService.getTitlecase('committees.edit.committee-details.terms.one-year');
        break;
      case TermSettingsEnum.TwoYears:
        this.defaultTermText = await this._translationService.getTitlecase('committees.edit.committee-details.terms.two-years');
        break;
      case TermSettingsEnum.ThreeYears:
        this.defaultTermText = await this._translationService.getTitlecase('committees.edit.committee-details.terms.three-years');
        break;
      default:
        this.defaultTermText = `${this.committee.TermMonths} ${await this._translationService.getTitlecase('common.month-names.months')}`;
    }
  }

  private differenceInMonths(date1: Date, date2: Date) {
    const yearsDiff = Math.abs(date1.getFullYear() - date2.getFullYear());
    const months = yearsDiff * 12 + Math.abs(date1.getMonth() - date2.getMonth());
    return months;
  }
}
